"use client";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  LiveStreamSection,
  LoadoutsSection,
  ProfileHeader,
  ProfileStats,
  TierListsSection,
  WeaponBuildsSection,
} from "./components";
import type { UserProfile } from "./utils";

type UserProfileContentProps = {
  profile: UserProfile;
};

// ponytail: no per-user class art in the data model, banner is decorative like the hero's static class render.
const DOSSIER_BANNER = "/battlefield-6-engineer-class-16x9.avif";

const TAB_TRIGGER_CLASS =
  "rounded-none border-transparent border-b-2 px-4 py-3.5 font-mono text-[#8B9297] text-xs tracking-wider data-[state=active]:border-[#F5A23E] data-[state=active]:bg-transparent data-[state=active]:text-[#EAEBEC] data-[state=active]:shadow-none";

function getDefaultTab(profile: UserProfile) {
  if (profile.stats.totalWeaponBuilds > 0) {
    return "builds";
  }
  if (profile.stats.totalLoadouts > 0) {
    return "loadouts";
  }
  return "tier-lists";
}

export function UserProfileContent({ profile }: UserProfileContentProps) {
  const defaultTab = getDefaultTab(profile);

  return (
    <div className="container mx-auto space-y-6 px-4 py-8">
      <div className="overflow-hidden rounded-[10px] border border-[#262A2D] bg-[#0C0D0E] shadow-2xl">
        {/* Dossier banner */}
        <div
          className="relative min-h-[150px] bg-[center_30%] bg-cover"
          style={{ backgroundImage: `url(${DOSSIER_BANNER})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#0C0D0E]/50 to-[#0C0D0E]" />
        </div>

        {/* Dossier header */}
        <div className="-mt-[52px] relative flex flex-col gap-6 px-6 pb-6 sm:flex-row sm:items-end sm:justify-between md:px-10">
          <ProfileHeader profile={profile} />
          <ProfileStats profile={profile} />
        </div>

        {/* Content tabs */}
        <Tabs className="gap-0" defaultValue={defaultTab}>
          <TabsList className="h-auto w-full justify-start gap-0 rounded-none border-[#1c1f22] border-b bg-transparent p-0 px-6 md:px-10">
            <TabsTrigger className={TAB_TRIGGER_CLASS} value="builds">
              WEAPON BUILDS
            </TabsTrigger>
            <TabsTrigger className={TAB_TRIGGER_CLASS} value="loadouts">
              LOADOUTS
            </TabsTrigger>
            <TabsTrigger className={TAB_TRIGGER_CLASS} value="tier-lists">
              TIER LISTS
            </TabsTrigger>
          </TabsList>

          <div className="px-6 py-8 md:px-10">
            <TabsContent value="builds">
              <WeaponBuildsSection profile={profile} />
            </TabsContent>
            <TabsContent value="loadouts">
              <LoadoutsSection profile={profile} />
            </TabsContent>
            <TabsContent value="tier-lists">
              <TierListsSection profile={profile} />
            </TabsContent>
          </div>
        </Tabs>
      </div>

      {/* Live Stream Section (shown only when user is live) */}
      <LiveStreamSection profile={profile} />
    </div>
  );
}
